/**
 * One role -> capability table, used by both the nav bar and the route guards.
 *
 * These were two hand-maintained lists before - `ACL` in Header.jsx and eight
 * `allowedRoles` arrays in App.jsx - and they had already drifted: the nav hid
 * CSV Upload from a HOG while the route still let them in, and Analyze was
 * granted to reviewers by an ACL entry whose <Link> had been commented out. A
 * hidden nav item is not access control, so the two halves disagreeing meant
 * the guard, not the nav, decided what was reachable.
 *
 * The backend mirror of this table lives in backend/services/auth_dependency.py.
 * Change one, change the other - the frontend decides what is *shown*, the
 * backend decides what is *allowed*, and only the backend is load-bearing.
 *
 * The four functional roles:
 *   Data Operator - CSV Upload, Upload Files (which contains Automated Evaluation)
 *   HOG           - Reviewer Assigner
 *   Instructor    - Review, scoped server-side to their assigned courses
 *   Admin         - everything, including Analytics and Rubric Management
 */

export const ROLE_ADMIN = 'qprs_admin';
export const ROLE_SUPER_ADMIN = 'qprs_super_admin';
export const ROLE_HOG = 'qprs_hog';
export const ROLE_DATA_OPERATOR = 'qprs_data_operator';
export const ROLE_INSTRUCTOR = 'qprs_reviewer';

// qprs_super_admin is not currently defined on the `qprs` Keycloak client, so
// Admin means qprs_admin in practice. Kept because it costs nothing and dropping
// it would silently demote anyone the role is later created for.
export const ADMIN_ROLES = [ROLE_ADMIN, ROLE_SUPER_ADMIN];

// qprs_leadership is deliberately absent everywhere. Its only capability was
// Analytics, which is now Admin-only.
export const CAPABILITIES = {
  csvUpload:          [...ADMIN_ROLES, ROLE_DATA_OPERATOR],
  // Covers Automated Evaluation too: it is a tab of the Upload Files page
  // now, not a page of its own, so it needs no capability of its own.
  uploadPapers:       [...ADMIN_ROLES, ROLE_DATA_OPERATOR],
  reviewerAssigner:   [...ADMIN_ROLES, ROLE_HOG],
  review:             [...ADMIN_ROLES, ROLE_INSTRUCTOR],
  analytics:          [...ADMIN_ROLES],
  rubricManagement:   [...ADMIN_ROLES],
  // Analyze Papers has no nav item and is not one of the four functional pages.
  // Its route still exists, so it is pinned to Admin rather than left reachable
  // by an instructor - which is what dropping qprs_reviewer here means.
  analyzePapers:      [...ADMIN_ROLES],
};

/**
 * An instructor and nothing more privileged. The backend mirror of this is
 * Principal.is_instructor in auth_dependency.py, and the two must agree: the
 * frontend locks the Reviewer email field on the strength of this, while the
 * backend narrows the listing to the instructor's assigned courses on the
 * strength of its own copy. An admin who also happens to hold qprs_reviewer is
 * not an instructor for either purpose - they keep the free-text field and the
 * unscoped listing.
 */
export const isInstructorOnly = (roles) => {
  const held = Array.isArray(roles) ? roles : [];
  return held.includes(ROLE_INSTRUCTOR) && !held.some((r) => ADMIN_ROLES.includes(r));
};

/** Does this role list satisfy the given capability? */
export const hasCapability = (roles, capability) => {
  const allowed = CAPABILITIES[capability] || [];
  return (Array.isArray(roles) ? roles : []).some((r) => allowed.includes(r));
};
